import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Database, DatabaseReference, equalTo, listVal, objectVal, orderByChild, query, ref, update } from '@angular/fire/database';
import { getToken, Messaging } from '@angular/fire/messaging';
import { CFGRequest } from '@cfg-interface/requests.interfaces';
import { catchError, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  NEW_REQUEST_SUBSCRIPTION: Subscription = new Subscription();
  DATABASE_PATH: string = 'CarFinderGeniePlatform/notifications/';

  // Detección de plataforma mejorada
  private isSafari: boolean = false;
  private isIOS: boolean = false;
  private isNotificationSupported: boolean = false;

  // 🔧 NUEVO: Sistema mejorado de control de duplicación
  private sentNotifications: Map<string, number> = new Map();
  private readonly NOTIFICATION_TTL = 30000; // 30 segundos (aumentado de 5)
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 segundo

  // 🔧 NUEVO: Control de requests en progreso
  private pendingNotifications: Set<string> = new Set();

  // 🔧 NUEVO: Cache de notificaciones recientes
  private recentNotificationCache: Map<string, number> = new Map();
  private readonly CACHE_TTL = 1000; // 1 minuto

  constructor(
              private messaging: Messaging,
              private DATABASE: Database,
              private httpClient: HttpClient
              ) {
    this.detectPlatform();
  }

  private detectPlatform(): void {
    // Detectar iOS de forma más robusta
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Verificar soporte de Notification API
    this.isNotificationSupported = 'Notification' in window &&
                                  typeof Notification.requestPermission === 'function';

    // // // console.log('🔔 CFG Notification Platform Detection:', {
    //   isIOS: this.isIOS,
    //   isNotificationSupported: this.isNotificationSupported,
    //   userAgent: navigator.userAgent
    // });
  }

  public requestiOSNotificationPermission(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      await Notification.requestPermission( async (permission: NotificationPermission) => {
        if (permission == 'granted') {
          await this.getNotificationToken().then((token: string) => {
            resolve(token);
          });
        }
      }).catch((error: any) => {
        reject(error)
      })
    });
  }

  public async requestNotificationPermissions(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Verificar si las notificaciones están soportadas
        if (!this.isNotificationSupported) {
          // // console.warn('🚫 Notification API no soportada en esta plataforma');
          if (this.isIOS) {
            // // console.info('💡 iOS: Las notificaciones web no están disponibles en Safari');
            // En iOS, resolver como exitoso pero sin notificaciones
            resolve();
            return;
          }
          reject(new Error('Notification API not supported'));
          return;
        }

        const permissions: NotificationPermission = await Notification.requestPermission();
        if (permissions === 'granted') {
          // // // console.log('✅ Permisos de notificación concedidos');
          resolve();
        } else if (permissions === 'denied') {
          // // console.warn('❌ Permisos de notificación denegados');
          reject(new Error('Notification permission denied'));
        } else {
          // // console.warn('⏸️ Permisos de notificación pendientes');
          reject(new Error('Notification permission default'));
        }
      } catch (error) {
        // // console.error('🔥 Error solicitando permisos de notificación:', error);
        reject(error);
      }
    });
  }

  public sendMessageEmailNotifications(notification_data: any): Promise<void> {
    return new Promise( async (resolve, reject) => {
      try {
        const NOTIFICATIONS_PAYLOAD: any = {
          email: notification_data.email,
          sender_name: notification_data.sender_name,
          app: notification_data.app,
          message: notification_data.message,
          timestamp: notification_data.timestamp
        };
        const PAYLOAD_AS_TEXT = JSON.stringify(NOTIFICATIONS_PAYLOAD);
        this.NEW_REQUEST_SUBSCRIPTION = this.httpClient.post(`${environment.cloud_functions_urls.sendMessageNotificationEmail}`, PAYLOAD_AS_TEXT).pipe(catchError((errorTest:HttpErrorResponse): any => {
          if (errorTest.error instanceof ErrorEvent) {
            reject();
          } else {
            reject();
          }
        })).subscribe((response: any) => {}, (error: any) => { reject() }, () => { resolve() });
      } catch (error) {
        reject(error);
      }
    });
  }

  public async getCurrentPlatform(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      resolve(this.isIOS);
    });
  }

  public updateNotificationStatus(id: string, uid: string): Promise<void> {
    return new Promise( async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.DATABASE, `${this.DATABASE_PATH}/${id}/recipient_uid/${uid}/`);
      update(DATABASE_REFERENCE, {
        status: 'readed'
      }).then(() => {
        resolve();
      }).catch((error: any) => {
        reject();
      })
    });
  }

  public async getNotificationToken(): Promise<string> {
    try {
      // En iOS, Firebase messaging puede tener limitaciones
      if (this.isIOS) {
        // // console.warn('⚠️ iOS: Firebase messaging puede tener limitaciones');
        // Intentar de todas formas, pero con manejo de errores mejorado
      }

      // Verificar si service worker está disponible
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker no soportado');
      }

      const swRegistration = await navigator.serviceWorker.register('/fcm/firebase-messaging-sw.js', {
        scope: '/fcm/'
      });
      // const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      //   type: 'module',
      //   scope: '/'
      // });

      // Esperar a que el service worker esté listo
      // await navigator.serviceWorker.ready;

      const token = await getToken(this.messaging, {
        serviceWorkerRegistration: swRegistration,
        vapidKey: environment.vapidkey
      });

      if (token) {
        // // // console.log('🎯 Token de notificación obtenido:', token.substring(0, 20) + '...');
        return token;
      } else {
        throw new Error('No se pudo obtener el token de notificación');
      }
    } catch (error) {
      // // console.error('🔥 Error registrando service worker o obteniendo token:', error);

      // En iOS, no fallar completamente
      if (this.isIOS) {
        // // console.info('💡 iOS: Continuando sin token de notificación');
        return '';
      }

      throw error;
    }
  }

  public getNotifications(): Observable<any[]> {
    const DATABASE_REFERENCE: DatabaseReference = ref(this.DATABASE, `${this.DATABASE_PATH}`);
    return objectVal(DATABASE_REFERENCE);
  }

  public sendPushNotifications(notification_data: any, tokens: string[]): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // 🔧 NUEVO: Crear ID único SIN timestamp para detectar duplicados reales
      const notificationId = this.createStableNotificationId(notification_data);
      try {

        // 🔧 NUEVO: Verificar si ya se está procesando esta notificación
        if (this.pendingNotifications.has(notificationId)) {
          // // console.log('🔔 Notificación ya en progreso, omitiendo:', notificationId);
          resolve();
          return;
        }

        // 🔧 NUEVO: Verificar cache de notificaciones recientes
        if (this.isNotificationRecentlyCached(notificationId)) {
          // // console.log('🔔 Notificación enviada recientemente (cache), omitiendo:', notificationId);
          resolve();
          return;
        }

        // 🔧 NUEVO: Verificar si ya se envió esta notificación
        if (this.isNotificationRecentlySent(notificationId)) {
          // // console.log('🔔 Notificación ya enviada recientemente, omitiendo:', notificationId);
          resolve();
          return;
        }

        // 🔧 NUEVO: Marcar como en progreso
        this.pendingNotifications.add(notificationId);

        // Filtrar tokens válidos (excluir tokens vacíos de iOS)
        const validTokens = tokens.filter(token => token && token.length > 0 && !token.startsWith('safari_'));

        // Si no hay tokens válidos, resolver (sin error)
        if (validTokens.length === 0) {
          // console.info('💡 No hay tokens válidos para push notifications (probablemente iOS)');
          this.pendingNotifications.delete(notificationId);
          resolve();
          return;
        }

        // 🔧 NUEVO: Marcar como enviada ANTES del envío
        this.markNotificationAsSent(notificationId);
        this.addToRecentCache(notificationId);

        const CURRENT_HOST: string = location.hostname;
        const NOTIFICATIONS_PAYLOAD: any = {
          title: notification_data.title,
          body: notification_data.body,
          tokens: validTokens,
          data: {
            notification_type: notification_data.is_chat == true ? 'chat_message' : 'new_request',
            recipient_uid: notification_data.recipient_uid,
            timestamp: Date.now(),
            // 🔧 NUEVO: ID estable para tracking
            stable_id: notificationId
          }
        };

        notification_data.is_chat == true ?
          NOTIFICATIONS_PAYLOAD.data['chat_id'] = notification_data.chat_id :
          NOTIFICATIONS_PAYLOAD.data['request_id'] = notification_data.request_id;

        const PAYLOAD_AS_TEXT = JSON.stringify(NOTIFICATIONS_PAYLOAD);

        // 🔧 NUEVO: Cancelar suscripción anterior si existe
        if (this.NEW_REQUEST_SUBSCRIPTION) {
          this.NEW_REQUEST_SUBSCRIPTION.unsubscribe();
        }
        this.NEW_REQUEST_SUBSCRIPTION = this.httpClient.post(
          `${environment.cloud_functions_urls.sendPushNotifications}`,
          PAYLOAD_AS_TEXT,
        ).pipe(
          catchError((errorTest: HttpErrorResponse): any => {
            // 🔧 NUEVO: Manejo de errores mejorado
            // console.error('🔥 Error enviando notificación:', errorTest);
            this.pendingNotifications.delete(notificationId);
            reject(errorTest);
          })
        ).subscribe(
          (response: any) => {
            // // console.log('✅ Notificación enviada exitosamente:', notificationId);
            this.pendingNotifications.delete(notificationId);
            resolve();
          },
          (error: any) => {
            // console.error('❌ Error en respuesta de notificación:', error);
            this.pendingNotifications.delete(notificationId);
            reject(error);
          },
          () => {
            this.pendingNotifications.delete(notificationId);
            resolve();
          }
        );

      } catch (error) {
        // console.error('🔥 Error general en sendPushNotifications:', error);
        this.pendingNotifications.delete(notificationId);
        reject(error);
      }
    });
  }

  public removeNotifications(notifications: any): Promise<void> {
    return new Promise( async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.DATABASE, this.DATABASE_PATH);
      update(DATABASE_REFERENCE, notifications).then(() => {
        resolve();
      }).catch((error: any) => {
        reject();
      })
    });
  }

  // Métodos helper para verificar capacidades
  public isIOSDevice(): boolean {
    return this.isIOS;
  }

  public isNotificationAPISupported(): boolean {
    return this.isNotificationSupported;
  }

  public canUseNotifications(): boolean {
    return this.isNotificationSupported && !this.isIOS;
  }

  public getCurrentNotificationPermission(): NotificationPermission {
    return Notification.permission;
  }

  public getNotificationCapabilities(): {
    isIOS: boolean;
    isSupported: boolean;
    canUse: boolean;
    message: string;
  } {
    return {
      isIOS: this.isIOS,
      isSupported: this.isNotificationSupported,
      canUse: this.canUseNotifications(),
      message: this.isIOS
        ? 'iOS Safari no soporta notificaciones web push'
        : this.isNotificationSupported
          ? 'Notificaciones disponibles'
          : 'Notificaciones no soportadas en este navegador'
    };
  }

  // Método mejorado para crear notificaciones locales
  public createLocalNotification(title: string, body: string, data?: any): void {
    try {
      // Verificar si las notificaciones están soportadas
      if (!this.canUseNotifications()) {
        // console.warn('⚠️ No se puede crear notificación local: API no soportada');
        return;
      }

      if (Notification.permission !== 'granted') {
        // console.warn('⚠️ Permisos de notificación no concedidos');
        return;
      }

      const notification = new Notification(title, {
        body: body,
        icon: '/assets/icons/icon-128x128.png',
        tag: data?.chat_id || data?.request_id,
        requireInteraction: false
      });

      notification.onclick = () => {
        window.focus();
        if (data?.chat_id) {
          window.location.href = `/chats?id=${data.chat_id}`;
        } else if (data?.request_id) {
          window.location.href = `/profile/my-leads?id=${data.request_id}`;
        }
        notification.close();
      };

      // Auto-cerrar después de 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);

    } catch (error) {
      // console.error('🔥 Error creando notificación local:', error);
    }
  }

  /**
   * 🔧 NUEVO: Crea un ID estable para la notificación (SIN timestamp)
   * Esto permite detectar duplicados reales
   */
  private createStableNotificationId(notification_data: any): string {
    if (notification_data.is_chat) {
      return `chat_${notification_data.chat_id}_${notification_data.recipient_uid}`;
    } else {
      return `request_${notification_data.request_id}_${notification_data.recipient_uid}`;
    }
  }

  /**
   * 🔧 NUEVO: Crea un ID único con timestamp para tracking
   * Mantiene compatibilidad con el sistema existente
   */
  private createNotificationId(notification_data: any): string {
    const stableId = this.createStableNotificationId(notification_data);
    return `${stableId}_${Date.now()}`;
  }

  /**
   * 🔧 NUEVO: Verifica si la notificación ya fue enviada recientemente
   */
  private isNotificationRecentlySent(notificationId: string): boolean {
    if (notificationId.startsWith('chat_')) {
    return false; // ⚡ nunca bloquear chats
  }
    const timestamp = this.sentNotifications.get(notificationId);
    if (!timestamp) return false;

    const now = Date.now();
    const timeDiff = now - timestamp;

    return timeDiff < this.NOTIFICATION_TTL;
  }

  /**
   * 🔧 NUEVO: Verifica cache de notificaciones recientes
   */
  private isNotificationRecentlyCached(notificationId: string): boolean {
    const timestamp = this.recentNotificationCache.get(notificationId);
    if (!timestamp) return false;

    const now = Date.now();
    const timeDiff = now - timestamp;

    return timeDiff < this.CACHE_TTL;
  }

  /**
   * 🔧 NUEVO: Agrega al cache de notificaciones recientes
   */
  private addToRecentCache(notificationId: string): void {
    this.recentNotificationCache.set(notificationId, Date.now());

    // Limpiar cache después del TTL
    setTimeout(() => {
      this.recentNotificationCache.delete(notificationId);
    }, this.CACHE_TTL);
  }

  /**
   * 🔧 NUEVO: Marca la notificación como enviada y programa su limpieza
   */
  private markNotificationAsSent(notificationId: string): void {
    this.sentNotifications.set(notificationId, Date.now());

    // Limpiar después del TTL para evitar acumulación de memoria
    setTimeout(() => {
      this.sentNotifications.delete(notificationId);
    }, this.NOTIFICATION_TTL);
  }

  /**
   * 🔧 NUEVO: Limpia todas las notificaciones pendientes
   */
  public clearPendingNotifications(): void {
    this.pendingNotifications.clear();
  }

  /**
   * 🔧 NUEVO: Obtiene estadísticas de notificaciones
   */
  public getNotificationStats(): {
    pending: number;
    sent: number;
    cached: number;
  } {
    return {
      pending: this.pendingNotifications.size,
      sent: this.sentNotifications.size,
      cached: this.recentNotificationCache.size
    };
  }
}

